import * as React from 'react';

import { maybeParseCalendarDate } from '@rematter/common/dateTime';
import { CurrencyCode } from '@rematter/frontend-lib-api-client';
import {
  DataGridColumn,
  GroupRow,
  Button,
  Money,
  LocalizedDate,
  Menu,
} from '@rematter/ui-web';
import { LucideDownload, LucideEllipsis, LucidePrinter } from 'lucide-react';

import { BillRecord } from '../../../_graphql/queryBillList';
import { useTranslations } from '../../../i18n/useTranslations';

const getFullName = (
  person?: {
    firstName?: string | null;
    lastName?: string | null;
  } | null,
) =>
  person ? [person.firstName, person.lastName].filter(Boolean).join(' ') : null;

type MenuRefs = (key: string) => React.RefObject<HTMLButtonElement | null>;

type UseBillListColumnsArgs = {
  menuRefs: MenuRefs;
  onPrintBills: (ids: string[]) => void;
  onDownloadBills: (ids: string[]) => void;
};

export const useBillListColumns = ({
  menuRefs,
  onPrintBills,
  onDownloadBills,
}: UseBillListColumnsArgs): DataGridColumn<BillRecord>[] => {
  const t = useTranslations();

  return React.useMemo(
    () => [
      {
        key: 'supplierBillNumber',
        header: <div>{t.customerBillListView.header.supplierBillNumber}</div>,
        width: 170,
        cell: (cell: { row: BillRecord }) => (
          <div className="text-right">#{cell.row.documentNumber}</div>
        ),
        groupCell: (group: GroupRow<BillRecord>) => (
          <div className="font-medium">
            {group.children?.at(0)?.supplier.name}
          </div>
        ),
      },
      {
        key: 'total',
        header: t.customerBillListView.header.total,
        align: 'right',
        cell: (cell: { row: BillRecord }) => (
          <div className="text-right">
            <Money
              amount={cell.row.amount.amount}
              currencyCode={cell.row.amount.currencyCode as CurrencyCode}
            />
          </div>
        ),
      },
      {
        key: 'payDate',
        header: t.customerBillListView.header.payDate,
        width: 100,
        cell: (cell: { row: BillRecord }) => (
          <LocalizedDate
            date={maybeParseCalendarDate(cell.row.documentDate)}
            className="justify-end"
            dateStyle="medium"
          />
        ),
      },
      {
        key: 'accountManager',
        header: t.customerBillListView.header.accountManager,
        width: 120,
        cell: () => null,
        groupCell: (group: GroupRow<BillRecord>) => (
          <div className="font-medium">
            {getFullName(
              group.children?.at(0)?.supplier.accountManagers?.at(0),
            )}
          </div>
        ),
      },
      {
        key: 'actions',
        header: t.customerBillListView.header.actions,
        width: 50,
        cell: (cell: { row: BillRecord }) => (
          <Menu
            placement="bottom end"
            menuClassName="min-w-30"
            trigger={
              <Button
                ref={menuRefs(cell.row.id)}
                variant="outline"
                color="neutral"
              >
                <LucideEllipsis size={16} />
              </Button>
            }
            items={[
              {
                label: 'Print Bills',
                icon: <LucidePrinter />,
                onAction: () => onPrintBills([cell.row.id]),
              },
              {
                label: 'Download Bills',
                icon: <LucideDownload />,
                onAction: () => onDownloadBills([cell.row.id]),
              },
            ]}
          />
        ),
        groupCell: (group: GroupRow<BillRecord>) => (
          <Menu
            placement="bottom end"
            menuClassName="min-w-30"
            trigger={
              <Button
                ref={menuRefs(`group-${String(group.groupKey)}`)}
                variant="outline"
                color="neutral"
              >
                <LucideEllipsis size={16} />
              </Button>
            }
            items={[
              {
                label: 'Print Bills',
                icon: <LucidePrinter />,
                onAction: () =>
                  onPrintBills(group.children?.map((bill) => bill.id) ?? []),
              },
              {
                label: 'Download Bills',
                icon: <LucideDownload />,
                onAction: () =>
                  onDownloadBills(group.children?.map((bill) => bill.id) ?? []),
              },
            ]}
          />
        ),
        pinned: 'right',
      },
    ],
    [t, menuRefs, onPrintBills, onDownloadBills],
  );
};
